import { BubbleMenu } from '@tiptap/extension-bubble-menu'
import tippy from 'tippy.js'

// Bubble Menu Extension for text formatting
export const BubbleMenuExtension = BubbleMenu.extend({
  name: 'bubbleMenu',

  addOptions() {
    return {
      ...this.parent?.(),
      mode: 'html',  // 'html' or 'markdown'
      element: null,
      tippyOptions: {
        duration: 100,
        placement: 'top',
        theme: 'bubble-menu',
        maxWidth: 'none',
        interactive: true,
      },
      shouldShow: ({ editor, view, state, oldState, from, to }) => {
        // 只在有選取文字時顯示
        return from !== to
      },
    }
  },
})

// 建立 Bubble Menu 的 HTML 結構
export function createBubbleMenuElement(editor, mode = 'html') {
  const menu = document.createElement('div')
  menu.className = 'bubble-menu'

  // 根據模式決定顯示哪些按鈕
  const buttons = getBubbleMenuButtons(mode)

  menu.innerHTML = buttons.map(button => {
    if (button.type === 'divider') {
      return '<div class="bubble-menu-divider"></div>'
    }

    return `
      <button
        type="button"
        class="bubble-menu-button"
        data-action="${button.action}"
        title="${button.title}"
      >
        ${button.icon}
      </button>
    `
  }).join('')

  // 綁定按鈕事件
  menu.querySelectorAll('.bubble-menu-button').forEach(button => {
    const action = button.getAttribute('data-action')
    const buttonConfig = buttons.find(b => b.action === action)

    if (buttonConfig && buttonConfig.command) {
      button.addEventListener('click', (e) => {
        e.preventDefault()
        // 從 menu.editor 取得 editor 引用（在 notion_controller.js 中設定）
        const currentEditor = menu.editor
        if (currentEditor) {
          buttonConfig.command(currentEditor)
        }
      })

      // 儲存按鈕配置，稍後綁定事件監聽
      button.buttonConfig = buttonConfig
    }
  })

  // 儲存 bindEvents 方法到 menu 元素，讓 controller 可以在 editor 準備好後調用
  menu.bindEvents = (editor) => {
    menu.querySelectorAll('.bubble-menu-button').forEach(button => {
      const buttonConfig = button.buttonConfig
      if (buttonConfig) {
        // 更新按鈕狀態（active/disabled）
        const updateHandler = () => updateButtonState(button, editor, buttonConfig)
        editor.on('selectionUpdate', updateHandler)
        editor.on('transaction', updateHandler)
      }
    })
  }

  return menu
}

// 更新按鈕狀態
function updateButtonState(button, editor, config) {
  // 檢查是否 active
  if (config.isActive && config.isActive(editor)) {
    button.classList.add('is-active')
  } else {
    button.classList.remove('is-active')
  }

  // 檢查是否 disabled
  if (config.isDisabled && config.isDisabled(editor)) {
    button.setAttribute('disabled', 'disabled')
  } else {
    button.removeAttribute('disabled')
  }
}

// 取得 Bubble Menu 按鈕配置（純 Markdown 模式）
function getBubbleMenuButtons(mode) {
  // 只支援 Markdown 的格式
  const markdownButtons = [
    {
      action: 'heading1',
      title: '標題 1',
      icon: 'H1',
      command: (editor) => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: (editor) => editor.isActive('heading', { level: 1 }),
    },
    {
      action: 'heading2',
      title: '標題 2',
      icon: 'H2',
      command: (editor) => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: (editor) => editor.isActive('heading', { level: 2 }),
    },
    {
      action: 'heading3',
      title: '標題 3',
      icon: 'H3',
      command: (editor) => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: (editor) => editor.isActive('heading', { level: 3 }),
    },
    {
      action: 'heading4',
      title: '標題 4',
      icon: 'H4',
      command: (editor) => editor.chain().focus().toggleHeading({ level: 4 }).run(),
      isActive: (editor) => editor.isActive('heading', { level: 4 }),
    },
    {
      type: 'divider'
    },
    {
      action: 'bold',
      title: '粗體 (Ctrl+B)',
      icon: '<strong>B</strong>',
      command: (editor) => editor.chain().focus().toggleBold().run(),
      isActive: (editor) => editor.isActive('bold'),
    },
    {
      action: 'italic',
      title: '斜體 (Ctrl+I)',
      icon: '<em>I</em>',
      command: (editor) => editor.chain().focus().toggleItalic().run(),
      isActive: (editor) => editor.isActive('italic'),
    },
    {
      action: 'strike',
      title: '刪除線',
      icon: '<s>S</s>',
      command: (editor) => editor.chain().focus().toggleStrike().run(),
      isActive: (editor) => editor.isActive('strike'),
    },
    {
      type: 'divider'
    },
    {
      action: 'code',
      title: '行內程式碼',
      icon: '<code>&lt;/&gt;</code>',
      command: (editor) => editor.chain().focus().toggleCode().run(),
      isActive: (editor) => editor.isActive('code'),
    },
    {
      action: 'link',
      title: '連結',
      icon: '🔗',
      command: (editor) => {
        const url = window.prompt('輸入連結網址:')
        if (url) {
          editor.chain().focus().setLink({ href: url }).run()
        }
      },
      isActive: (editor) => editor.isActive('link'),
    },
  ]

  return markdownButtons
}
